// Regras puras da tela Talk.ai (/prompts) e do modal de agente.
// Separadas dos componentes para ficarem testáveis sem montar o React.
import { i18n } from "../../translate/i18n";
import { AEROS } from "../../styles/aerosTokens";

// Valores gravados em `Prompt.tone` — contrato do backend
// (BuildAgentSystemPromptService.TONS). Não renomear.
export const TONES = ["formal", "amigavel", "descontraido", "tecnico"];

export const toneLabel = (tone) =>
  i18n.t(`aeros.talkai.tones.${TONES.includes(tone) ? tone : "amigavel"}`);

// Modelos oferecidos no select da aba Avançado.
export const MODELS = ["gpt-4.1-mini", "gpt-4.1", "gpt-4o-mini"];

// Vozes Azure disponíveis; "texto" significa responder sem áudio.
export const VOICES = [
  "pt-BR-FranciscaNeural",
  "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural",
  "pt-BR-DonatoNeural",
  "pt-BR-ElzaNeural",
  "pt-BR-FabioNeural",
  "pt-BR-GiovannaNeural",
  "pt-BR-HumbertoNeural",
  "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural",
  "pt-BR-LeticiaNeural",
  "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural",
  "pt-BR-ValerioNeural",
  "pt-BR-YaraNeural",
];

/**
 * "Francisca" a partir de "pt-BR-FranciscaNeural".
 *
 * O nome curto é o que a lista e o modal mostram; guardar o id completo é o
 * que o backend manda para o Azure.
 */
export const voiceShortName = (voice) => {
  if (!voice || voice === "texto") return "";
  const match = /^pt-BR-(.+)Neural$/.exec(voice);
  return match ? match[1] : voice;
};

export const usesVoice = (voice) => !!voice && voice !== "texto";

// Ícone e tom do ladrilho do agente na lista. Determinístico pelo id: o mesmo
// agente mantém a aparência entre recarregamentos.
const TILES = [
  { icon: "support_agent", color: AEROS.cyan },
  { icon: "storefront", color: "#C08BFF" },
  { icon: "receipt_long", color: AEROS.success },
  { icon: "call_split", color: AEROS.warning },
  { icon: "home_repair_service", color: AEROS.error },
];

export const agentTile = (id = 0) =>
  TILES[Math.abs(Number(id) || 0) % TILES.length];

/**
 * Estado da coluna "Conhecimento" na lista.
 *
 * "Processando" vence a contagem de prontos: enquanto houver arquivo sendo
 * indexado, o agente ainda não sabe tudo o que vai saber, e é isso que a
 * pessoa precisa ver.
 */
export const knowledgeState = (files = []) => {
  if (files.some((f) => f.status === "processing")) return "processing";
  const prontos = files.filter((f) => f.status === "ready").length;
  return prontos > 0 ? "ready" : "none";
};

export const knowledgeLabel = (files = []) => {
  const estado = knowledgeState(files);
  if (estado === "processing") return i18n.t("aeros.talkai.knowledge.processing");
  if (estado === "none") return i18n.t("aeros.talkai.knowledge.none");
  const prontos = files.filter((f) => f.status === "ready").length;
  return i18n.t(
    prontos === 1 ? "aeros.talkai.knowledge.fileOne" : "aeros.talkai.knowledge.files",
    { count: prontos }
  );
};

/**
 * Resumo dos arquivos para o rodapé da aba Conhecimento.
 *
 * `firstFailure` alimenta a linha vermelha do mockup, que nomeia o arquivo e
 * o motivo — "falhou" sem dizer qual arquivo não ajuda ninguém.
 */
export const filesSummary = (files = []) => {
  const ready = files.filter((f) => f.status === "ready").length;
  const processing = files.filter((f) => f.status === "processing").length;
  const failedFiles = files.filter((f) => f.status === "failed");
  return {
    ready,
    processing,
    failed: failedFiles.length,
    firstFailure: failedFiles[0] || null,
  };
};

/**
 * Roteiro da "Simulação de conversa" da aba Personalidade.
 *
 * É ilustração, não chamada à IA: mostra como o tom, os emojis e o nome do
 * agente mudam a resposta, e como a regra de transferência encerra a conversa.
 * Gerar de verdade custaria uma chamada paga a cada tecla digitada.
 */
export const simulationScript = ({
  agentName,
  tone,
  useEmojis,
  transferMessage,
  transferInstructions,
  queueName,
} = {}) => {
  const nome = agentName || i18n.t("aeros.talkai.simulation.defaultName");
  const emoji = useEmojis ? " 🙂" : "";
  const abertura = i18n.t(
    `aeros.talkai.simulation.greeting.${TONES.includes(tone) ? tone : "amigavel"}`,
    { name: nome }
  );

  const roteiro = [
    { from: "contact", text: i18n.t("aeros.talkai.simulation.contact1") },
    { from: "agent", text: `${abertura}${emoji} ${i18n.t("aeros.talkai.simulation.agent1")}` },
    { from: "contact", text: i18n.t("aeros.talkai.simulation.contact2") },
    {
      from: "agent",
      text:
        transferMessage ||
        i18n.t("aeros.talkai.simulation.defaultTransfer", {
          queue: queueName || i18n.t("aeros.talkai.simulation.defaultQueue"),
        }),
    },
  ];

  return {
    messages: roteiro,
    // A etiqueta final cita a regra que disparou a transferência; sem regra
    // configurada, a IA transferiria só por não saber responder.
    transferReason: transferInstructions
      ? i18n.t("aeros.talkai.simulation.transferredByRule", {
          queue: queueName || i18n.t("aeros.talkai.simulation.defaultQueue"),
        })
      : i18n.t("aeros.talkai.simulation.transferredNoAnswer", {
          queue: queueName || i18n.t("aeros.talkai.simulation.defaultQueue"),
        }),
  };
};

// Limite do campo de conhecimento em texto, espelhando o contador do mockup.
export const KNOWLEDGE_LIMIT = 8000;

export const knowledgeCounter = (texto = "") =>
  `${(texto || "").length.toLocaleString()} / ${KNOWLEDGE_LIMIT.toLocaleString()}`;

// Tamanho legível do arquivo importado ("1,8 MB", "42 KB").
export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Vínculo do agente para a coluna "Conexão · Filas" e o diálogo de exclusão.
 *
 * `pendente` também vale quando o registro não traz o campo: agente sem
 * conexão é legado e precisa ser editado para escolher uma.
 */
export const vinculoResumo = (prompt) => {
  const filas = Array.isArray(prompt?.queues) ? prompt.queues : [];
  const pendente = !!prompt?.pendente || !prompt?.whatsappId;
  return {
    pendente,
    conexao: pendente ? "" : prompt?.whatsapp?.name || "",
    filas,
    antesDaFila: !!prompt?.atendeAntesDaFila,
  };
};

export const vinculoTexto = (prompt) => {
  const r = vinculoResumo(prompt);
  const cabeca = r.pendente
    ? i18n.t("aeros.talkai.vinculo.pending")
    : r.conexao || "—";
  const itens = r.filas.map((q) => q.name);
  if (r.antesDaFila) itens.push(i18n.t("aeros.talkai.vinculo.preQueue"));
  return itens.length ? `${cabeca} · ${itens.join(", ")}` : cabeca;
};
